import { ObjectGeometry } from '../../object/object-geometry';
import { CanvasRenderContext } from '../types/canvas-object';
import {
  ObjectEvents,
  ObjectProps,
  BaseProps,
  basePropsDefaults,
  fillStrokePropsDefaults,
  renderPropsDefaults,
  serializationPropsDefaults,
  statePropsDefaults,
} from './types';

/**
 * Base class for all image editor objects
 *
 * EditorObject extends ObjectGeometry, adding functionality for:
 * - Geometric transformations (rotation, scale, skew) - from ObjectGeometry
 * - Positioning and dimensions - from ObjectGeometry
 * - Styling (fill, stroke, shadow)
 * - Events via EventBus
 * - Serialization and deserialization
 *
 * SSR-compatible - does not use DOM APIs directly
 *
 * @example
 * ```typescript
 * const obj = new EditorObject({
 *   left: 100,
 *   top: 100,
 *   width: 200,
 *   height: 150,
 *   fill: '#ff0000',
 * });
 *
 * obj.on('object:modified', (e) => {
 *   console.log('Object modified:', e);
 * });
 *
 * obj.set('angle', 45);
 * obj.scale(2);
 * ```
 */
export class EditorObject<EventSpec extends ObjectEvents = ObjectEvents>
  extends ObjectGeometry<EventSpec>
  implements ObjectProps
{
  // BaseProps
  declare left: number;
  declare top: number;
  declare width: number;
  declare height: number;
  declare originX: BaseProps['originX'];
  declare originY: BaseProps['originY'];
  declare angle: number;
  declare flipX: boolean;
  declare flipY: boolean;
  declare scaleX: number;
  declare scaleY: number;
  declare skewX: number;
  declare skewY: number;

  // FillStrokeProps
  declare fill: string | null;
  declare fillRule: CanvasFillRule;
  declare stroke: string | null;
  declare strokeWidth: number;
  declare strokeDashArray: number[] | null;
  declare strokeDashOffset: number;
  declare strokeLineCap: CanvasLineCap;
  declare strokeLineJoin: CanvasLineJoin;
  declare strokeMiterLimit: number;
  declare paintFirst: 'fill' | 'stroke';

  // RenderProps
  declare opacity: number;
  declare globalCompositeOperation: GlobalCompositeOperation;
  declare backgroundColor: string;
  declare visible: boolean;
  declare shadow: string | null;

  // SerializationProps
  declare includeDefaultValues: boolean;
  declare excludeFromExport: boolean;

  // StateProps
  declare id?: string;
  declare type: string;
  declare data?: Record<string, unknown>;
  declare dirty: boolean;

  constructor(options?: Partial<ObjectProps>) {
    super();

    // Initialize default values
    this._initializeDefaults();

    // Apply provided options
    if (options) {
      this._setOptions(options);
    }

    // Generate an ID if none was provided
    if (!this.id) {
      this.id = this._generateId();
    }
  }

  /**
   * Initializes properties with default values
   * @private
   */
  private _initializeDefaults(): void {
    // BaseProps defaults
    Object.assign(this, basePropsDefaults);

    // FillStrokeProps defaults
    Object.assign(this, fillStrokePropsDefaults);

    // RenderProps defaults
    Object.assign(this, renderPropsDefaults);

    // SerializationProps defaults
    Object.assign(this, serializationPropsDefaults);

    // StateProps defaults
    Object.assign(this, statePropsDefaults);
  }

  /**
   * Generates a unique ID for the object
   * @private
   */
  private _generateId(): string {
    return `${this.type}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Type-safely fires an event from the base ObjectEvents map.
   *
   * EventSpec extends ObjectEvents, but TypeScript cannot prove this
   * correspondence across generic keys — the only place with a cast.
   */
  protected fireObjectEvent<K extends keyof ObjectEvents>(
    eventName: K,
    event: ObjectEvents[K]
  ): void {
    this.fire(
      eventName as unknown as keyof EventSpec,
      event as unknown as EventSpec[keyof EventSpec]
    );
  }

  /**
   * Overrides set to track changes
   */
  override set(key: string, value?: unknown): this;
  override set(key: Record<string, unknown>): this;
  override set(key: string | Record<string, unknown>, value?: unknown): this {
    const oldValues: Record<string, unknown> = {};

    // Save old values
    if (typeof key === 'string') {
      oldValues[key] = this.get(key);
    } else {
      for (const prop in key) {
        oldValues[prop] = this.get(prop);
      }
    }

    // Call the parent set
    super.set(key, value);

    // Mark the object as modified
    this.dirty = true;

    // Fire property change events
    if (typeof key === 'string') {
      this._firePropertyChanged(key, oldValues[key], value);
    } else {
      for (const prop in key) {
        this._firePropertyChanged(prop, oldValues[prop], key[prop]);
      }
    }

    return this;
  }

  /**
   * Fires a property change event
   * @private
   */
  private _firePropertyChanged(property: string, oldValue: unknown, newValue: unknown): void {
    if (oldValue !== newValue) {
      this.fireObjectEvent('property:changed', {
        target: this,
        property,
        oldValue,
        newValue,
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Overrides scale from ObjectGeometry, adding events
   * @override
   */
  override scale(value: number): this {
    super.scale(value);
    this.dirty = true;
    this.fireObjectEvent('object:scaled', {
      target: this,
      action: 'scaling',
      timestamp: Date.now(),
    });
    return this;
  }

  /**
   * Overrides setCoords to set the dirty flag
   * @override
   */
  override setCoords(): this {
    super.setCoords();
    this.dirty = true;
    return this;
  }

  /**
   * Clones the object
   * @returns A new object instance with the same properties
   */
  clone(): EditorObject {
    const cloned = new EditorObject(this.toObject());
    cloned.id = this._generateId();
    return cloned;
  }

  /**
   * Base render method. Subclasses should override with drawing logic.
   */
   
  render(_ctx: CanvasRenderingContext2D, _context: CanvasRenderContext): void {
    // Default implementation intentionally left blank.
  }

  /**
   * Serializes the object into a plain object
   * @returns An object with all properties
   */
  toObject(): Partial<ObjectProps> {
    const obj: Partial<ObjectProps> = {
      id: this.id,
      type: this.type,
      left: this.left,
      top: this.top,
      width: this.width,
      height: this.height,
      originX: this.originX,
      originY: this.originY,
      angle: this.angle,
      flipX: this.flipX,
      flipY: this.flipY,
      scaleX: this.scaleX,
      scaleY: this.scaleY,
      skewX: this.skewX,
      skewY: this.skewY,
      fill: this.fill,
      fillRule: this.fillRule,
      stroke: this.stroke,
      strokeWidth: this.strokeWidth,
      strokeDashArray: this.strokeDashArray,
      strokeDashOffset: this.strokeDashOffset,
      strokeLineCap: this.strokeLineCap,
      strokeLineJoin: this.strokeLineJoin,
      strokeMiterLimit: this.strokeMiterLimit,
      paintFirst: this.paintFirst,
      opacity: this.opacity,
      globalCompositeOperation: this.globalCompositeOperation,
      backgroundColor: this.backgroundColor,
      visible: this.visible,
      shadow: this.shadow,
      data: this.data,
    };

    // Exclude default values if needed
    if (!this.includeDefaultValues) {
      return this._removeDefaultValues(obj);
    }

    return obj;
  }

  /**
   * Removes default values from the object
   * @private
   */
  private _removeDefaultValues(obj: Partial<ObjectProps>): Partial<ObjectProps> {
    const defaults = {
      ...basePropsDefaults,
      ...fillStrokePropsDefaults,
      ...renderPropsDefaults,
      ...serializationPropsDefaults,
      ...statePropsDefaults,
    };

    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (obj[key as keyof ObjectProps] !== defaults[key as keyof typeof defaults]) {
        result[key] = obj[key as keyof ObjectProps];
      }
    }

    return result as Partial<ObjectProps>;
  }

  /**
   * Serializes the object to JSON
   * @returns JSON string
   */
  toJSON(): string {
    return JSON.stringify(this.toObject());
  }

  /**
   * Creates an object from a plain object
   * @param object - An object with properties
   * @returns A new EditorObject instance
   */
  static fromObject(object: Partial<ObjectProps>): EditorObject {
    return new EditorObject(object);
  }

  /**
   * Creates an object from JSON
   * @param json - JSON string
   * @returns A new EditorObject instance
   */
  static fromJSON(json: string): EditorObject {
    const obj = JSON.parse(json);
    return EditorObject.fromObject(obj);
  }

  /**
   * Releases the object's resources
   */
  dispose(): void {
    // Remove all event handlers
    this.off();
  }
}
